import {
  COUNTRY_LABELS,
  COUNTRY_CODES,
  WORK_MODE_LABELS,
  WORK_MODE_VALUES,
  JOB_TYPE_LABELS,
  JOB_TYPE_VALUES,
} from "./constants";
import type { JobsApiResponse, JobBySlugResponse } from "./types";
import type { JobsResponseDto } from "@artha/api-contracts";
import type { JobBySlugItemDto } from "./types";

// ============================================
// Value Normalization Functions
// ============================================

export function normalizeCountryCode(value: string): string {
  if (!value) return "";
  const trimmed = value.trim();
  return (
    COUNTRY_CODES[trimmed] ||
    COUNTRY_CODES[trimmed.toLowerCase()] ||
    trimmed.toUpperCase()
  );
}

export function getCountryLabel(value: string): string {
  return COUNTRY_LABELS[value] || value;
}

export function normalizeWorkMode(value: string): string {
  return WORK_MODE_VALUES[value] || value.toLowerCase();
}

export function getWorkModeLabel(value: string): string {
  return WORK_MODE_LABELS[value] || value;
}

export function normalizeJobType(value: string): string {
  return JOB_TYPE_VALUES[value] || value.toLowerCase();
}

export function getJobTypeLabel(value: string): string {
  return JOB_TYPE_LABELS[value] || value;
}

export function normalizeNicheValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getNicheLabel(value: string): string {
  if (value.includes(" ") || value.includes("&")) return value;
  
  return value
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Sort by normalization - maps frontend sort options to API values
const SORT_BY_VALUES: Record<string, string> = {
  relevant: "most_relevant",
  high_epa: "high_cpa",
  newest: "newest",
  most_relevant: "most_relevant",
  high_cpa: "high_cpa",
  high_rpa: "high_cpa",
  high_epc: "high_cpc",
  high_cpc: "high_cpc",
  high_rpc: "high_cpc",
  high_eph: "high_rph",
  high_rph: "high_rph",
};

export function normalizeSortBy(value: string): string {
  return SORT_BY_VALUES[value] || "most_relevant";
}

// ============================================
// Response Normalization Functions
// ============================================

export const normalizeJobsResponse = (
  payload?: JobsApiResponse
): JobsResponseDto | undefined => {
  if (!payload) return undefined;
  if ("data" in payload) return payload.data;
  if ("jobs" in payload) {
    return {
      items: payload.jobs ?? [],
      total: payload.total ?? payload.jobs?.length ?? 0,
      limit: payload.limit ?? payload.jobs?.length ?? 0,
      offset: payload.offset ?? 0,
      has_more: payload.has_more ?? false,
    };
  }
  return payload;
};

export const normalizeJobBySlugResponse = (
  payload?: JobBySlugResponse
): JobBySlugItemDto | undefined => {
  if (!payload) return undefined;
  if ("data" in payload) return payload.data;
  return payload;
};
